'use client'

import { useState, useCallback, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { useToast } from '@/components/ui/use-toast'

import { PriceLineItemsSelector, type PriceLineItem } from '@/components/form/price-line-items-selector'
import { PromotionsSelector } from '@/components/form/offer/promotions-selector'

import { usePrice } from '@/lib/api/prices'
import { useStore } from '@/lib/api/stores'
import { useCreatePromotion, usePromotion, type CreatePromotionInput } from '@/lib/api/promotions'
import { useCreateOffer, useUpdateOffer, useOffer, type CreateOfferInput, type UpdateOfferInput } from '@/lib/api/offers'
import { getPromotionDiscount, type Discount } from '@/lib/promotion-discount'

import { ClubOfferCardView } from './ClubOfferCardView'

type DiscountMode = 'new' | 'existing'
type DiscountType = 'percentage' | 'amount'

interface ClubOfferFormProps {
    storeId: string
    /** When set, edits this offer; otherwise creates a new one. */
    offerId?: string | null
    /** Called after a successful create/update. */
    onSaved?: () => void
    /** Optional cancel button (e.g. inside a modal). */
    onCancel?: () => void
    /** Hide the built-in action row (parent renders its own, e.g. wizard footer). */
    hideActions?: boolean
    /** Reports submit-availability so a parent footer can drive the submit button. */
    onStateChange?: (state: { canSubmit: boolean; isSubmitting: boolean }) => void
}

export interface ClubOfferFormHandle {
    submit: () => void
}

/** Resolves a single price's unit amount (minor units) and reports it upward. */
const PriceProbe = ({
    priceId,
    currency,
    onResolve,
}: {
    priceId: string
    currency: string
    onResolve: (priceId: string, amount: number | null) => void
}) => {
    const { data: price } = usePrice(priceId, true)
    const amount = price?.currencyOptions?.[currency]?.amount ?? null
    useEffect(() => {
        onResolve(priceId, amount)
    }, [priceId, amount, onResolve])
    return null
}

/** Resolves a selected promotion's discount (minor units) and reports it upward. */
const PromoDiscountProbe = ({
    promotionId,
    storeId,
    currency,
    onResolve,
}: {
    promotionId: string
    storeId: string
    currency: string
    onResolve: (id: string, discount: Discount | null) => void
}) => {
    const { data: details } = usePromotion(promotionId, storeId)
    const discount = getPromotionDiscount(details?.actions, currency)
    const key = JSON.stringify(discount)
    useEffect(() => {
        onResolve(promotionId, discount)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promotionId, key, onResolve])
    return null
}

/**
 * The full club-offer create/edit form: products, member discount (new or
 * existing), details, and a live member preview. Renders inline (no dialog),
 * so it can be embedded in the modal or the setup wizard.
 */
export const ClubOfferForm = forwardRef<ClubOfferFormHandle, ClubOfferFormProps>(function ClubOfferForm(
    { storeId, offerId, onSaved, onCancel, hideActions, onStateChange },
    ref,
) {
    const { t } = useTranslation('club')
    const { toast } = useToast()
    const isEdit = !!offerId

    const { data: store } = useStore(storeId)
    const currency = store?.baseCurrency || 'USD'

    const createPromotion = useCreatePromotion()
    const createOffer = useCreateOffer()
    const updateOffer = useUpdateOffer()
    const { data: offerResp } = useOffer(offerId || '', isEdit)
    const offerDetails = offerResp?.offer

    const [lineItems, setLineItems] = useState<PriceLineItem[]>([])
    const [titleEn, setTitleEn] = useState('')
    const [titleEdited, setTitleEdited] = useState(false)
    const [expiry, setExpiry] = useState<string | null>(null)
    const [discountMode, setDiscountMode] = useState<DiscountMode>('new')
    const [discountType, setDiscountType] = useState<DiscountType>('percentage')
    const [discountValue, setDiscountValue] = useState<number>(10)
    // Optional explicit credit price for redeeming the whole offer (in credits).
    // null = leave unset; the storefront then falls back to the store credit ratio.
    const [creditPrice, setCreditPrice] = useState<number | null>(null)
    const [promotionIds, setPromotionIds] = useState<string[]>([])
    const [priceAmounts, setPriceAmounts] = useState<Record<string, number | null>>({})

    const [loadedOfferId, setLoadedOfferId] = useState<string | null>(null)
    const showLoading = isEdit && loadedOfferId !== offerId

    // Populate the form once when editing and the offer has loaded.
    useEffect(() => {
        if (!isEdit || !offerDetails) return
        if (loadedOfferId === offerId) return
        setLoadedOfferId(offerId ?? null)

        setTitleEn(offerDetails.titleTrans?.en || '')
        setTitleEdited(true)
        setExpiry(offerDetails.expiryDate ? new Date(offerDetails.expiryDate).toISOString() : null)
        // getOfferById returns the full offer row (incl. credit_price); the generated
        // tRPC output type lags the schema here, so read it through a narrow cast.
        setCreditPrice((offerDetails as { creditPrice?: number | null }).creditPrice ?? null)
        setLineItems(
            (offerDetails.offerLineItems || []).map((li) => ({
                productId: li.price?.variant?.product?.id || '',
                productName: li.price?.variant?.product?.name || '',
                variantId: li.price?.variantId || li.price?.variant?.id || '',
                variantName: li.price?.variant?.name || '',
                imageUrl: li.price?.variant?.imageUrl ?? null,
                priceId: li.price?.id || '',
                quantity: li.quantity,
            })),
        )
        const promoIds = (offerDetails.promotions || [])
            .map((p) => p.promotion?.id)
            .filter((id): id is string => !!id)
        if (promoIds.length) {
            setDiscountMode('existing')
            setPromotionIds(promoIds)
        }
    }, [isEdit, offerId, offerDetails, loadedOfferId])

    // Auto-title from first product (create mode only, until user types).
    useEffect(() => {
        if (titleEdited) return
        const first = lineItems[0]
        setTitleEn(first?.productName ? `${first.productName} Club Deal` : '')
    }, [lineItems, titleEdited])

    const handlePriceResolve = useCallback((priceId: string, amount: number | null) => {
        setPriceAmounts((prev) => (prev[priceId] === amount ? prev : { ...prev, [priceId]: amount }))
    }, [])

    const regularMinor = useMemo(
        () => lineItems.reduce((sum, li) => sum + (priceAmounts[li.priceId] ?? 0) * li.quantity, 0),
        [lineItems, priceAmounts],
    )
    const pricesResolved =
        lineItems.length > 0 && lineItems.every((li) => typeof priceAmounts[li.priceId] === 'number')

    const [promoDiscounts, setPromoDiscounts] = useState<Record<string, Discount | null>>({})
    const handlePromoDiscountResolve = useCallback((id: string, discount: Discount | null) => {
        setPromoDiscounts((prev) =>
            JSON.stringify(prev[id] ?? null) === JSON.stringify(discount ?? null) ? prev : { ...prev, [id]: discount },
        )
    }, [])

    const clubMinor = useMemo(() => {
        if (discountMode === 'new') {
            if (discountType === 'percentage') {
                return Math.max(0, Math.round(regularMinor * (1 - discountValue / 100)))
            }
            return Math.max(0, regularMinor - Math.round(discountValue * 100))
        }
        const totalOff = promotionIds.reduce((off, id) => {
            const d = promoDiscounts[id]
            if (!d) return off
            if (d.kind === 'percentage') return off + (regularMinor * d.percent) / 100
            if (d.kind === 'amount') return off + d.minor
            return off
        }, 0)
        return Math.max(0, Math.round(regularMinor - totalOff))
    }, [discountMode, discountType, discountValue, regularMinor, promotionIds, promoDiscounts])

    const missingDiscountInfo =
        discountMode === 'existing' && promotionIds.some((id) => promoDiscounts[id] === null)

    const canSubmit =
        lineItems.length > 0 &&
        titleEn.trim().length > 0 &&
        (discountMode === 'existing' ? promotionIds.length > 0 : discountValue > 0)

    const isSubmitting = createPromotion.isPending || createOffer.isPending || updateOffer.isPending

    const handleSubmit = async () => {
        try {
            let attach = promotionIds

            if (discountMode === 'new') {
                const action =
                    discountType === 'percentage'
                        ? {
                              type: 'OrderAdjustment' as const,
                              adjustmentType: 'percentage' as const,
                              adjustmentPercentage: discountValue,
                          }
                        : {
                              type: 'OrderAdjustment' as const,
                              adjustmentType: 'amount' as const,
                              adjustmentAmount: { [currency]: { amount: Math.round(discountValue * 100) } },
                          }

                const promoInput: CreatePromotionInput = {
                    storeId,
                    name: `${titleEn.trim()} – Club discount`,
                    automatic: false,
                    enabled: true,
                    rules: [],
                    actions: [action],
                }
                const promo = await createPromotion.mutateAsync(promoInput)
                attach = [promo.id]
            }

            if (isEdit && offerId) {
                const input: UpdateOfferInput = {
                    type: 'club',
                    offerId,
                    storeId,
                    titleTrans: { en: titleEn.trim() },
                    expiryDate: expiry ? new Date(expiry) : undefined,
                    creditPrice: creditPrice ?? undefined,
                    lineItems: lineItems.map((li) => ({ priceId: li.priceId, quantity: li.quantity })),
                    promotionIds: attach,
                }
                await updateOffer.mutateAsync(input)
            } else {
                const input: CreateOfferInput = {
                    storeId,
                    type: 'club',
                    titleTrans: { en: titleEn.trim() },
                    expiryDate: expiry ? new Date(expiry) : undefined,
                    creditPrice: creditPrice ?? undefined,
                    lineItems: lineItems.map((li) => ({
                        priceId: li.priceId,
                        quantity: li.quantity,
                        variantId: li.variantId,
                    })),
                    promotionIds: attach,
                }
                await createOffer.mutateAsync(input)
            }

            toast({
                title: isEdit ? t('offers.toasts.updatedTitle') : t('offers.toasts.createdTitle'),
                description: isEdit ? t('offers.toasts.updatedDesc') : t('offers.toasts.createdDesc'),
            })
            onSaved?.()
        } catch {
            // create/update hooks already surface an error toast
        }
    }

    const previewImage = lineItems[0]?.imageUrl ?? null

    useImperativeHandle(ref, () => ({ submit: handleSubmit }))

    useEffect(() => {
        onStateChange?.({ canSubmit, isSubmitting })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSubmit, isSubmitting])

    if (showLoading) {
        return (
            <div className="flex h-56 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_300px]">
                {/* Form column */}
                <div className="min-w-0 space-y-6">
                    {/* Products */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-semibold">{t('offers.modal.products')}</h3>
                        <PriceLineItemsSelector
                            storeId={storeId}
                            lineItems={lineItems}
                            onLineItemsChange={setLineItems}
                            label={t('offers.modal.selectProducts')}
                            required
                        />
                    </section>

                    {/* Member discount */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-semibold">{t('offers.modal.discount')}</h3>
                        <Select value={discountMode} onValueChange={(v) => setDiscountMode(v as DiscountMode)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="new">{t('offers.modal.discountNew')}</SelectItem>
                                <SelectItem value="existing">{t('offers.modal.discountExisting')}</SelectItem>
                            </SelectContent>
                        </Select>

                        {discountMode === 'new' ? (
                            <div className="flex items-end gap-2">
                                <div className="w-44 shrink-0 space-y-1">
                                    <Label>{t('offers.modal.discountType')}</Label>
                                    <Select value={discountType} onValueChange={(v) => setDiscountType(v as DiscountType)}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="percentage">{t('offers.modal.percentOff')}</SelectItem>
                                            <SelectItem value="amount">
                                                {t('offers.modal.amountOff', { currency })}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex-1 space-y-1">
                                    <Label htmlFor="discount-value">{t('offers.modal.value')}</Label>
                                    <Input
                                        id="discount-value"
                                        type="number"
                                        min="0"
                                        step={discountType === 'percentage' ? '1' : '0.01'}
                                        value={discountValue}
                                        onChange={(e) => setDiscountValue(Number(e.target.value))}
                                    />
                                </div>
                            </div>
                        ) : (
                            <PromotionsSelector
                                storeId={storeId}
                                promotionIds={promotionIds}
                                onPromotionIdsChange={setPromotionIds}
                            />
                        )}
                    </section>

                    {/* Details */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-semibold">{t('offers.modal.details')}</h3>
                        <div className="space-y-1">
                            <Label htmlFor="offer-title">{t('offers.modal.offerTitle')}</Label>
                            <Input
                                id="offer-title"
                                value={titleEn}
                                onChange={(e) => {
                                    setTitleEdited(true)
                                    setTitleEn(e.target.value)
                                }}
                                placeholder={t('offers.modal.offerTitlePlaceholder')}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label>{t('offers.modal.expiry')}</Label>
                            <DatePicker
                                value={expiry ? new Date(expiry) : null}
                                onChange={(d) => setExpiry(d?.toISOString() ?? null)}
                                placeholder={t('offers.modal.expiryPlaceholder')}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="offer-credit-price">{t('offers.modal.creditPrice')}</Label>
                            <Input
                                id="offer-credit-price"
                                type="number"
                                min="1"
                                step="1"
                                value={creditPrice ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value
                                    setCreditPrice(v === '' ? null : Math.max(1, Math.floor(Number(v))))
                                }}
                                placeholder={t('offers.modal.creditPricePlaceholder')}
                            />
                            <p className="text-muted-foreground text-xs">{t('offers.modal.creditPriceHelp')}</p>
                        </div>
                    </section>
                </div>

                {/* Live preview column — hidden on small screens */}
                <div className="hidden space-y-2 md:block md:self-start md:sticky md:top-0">
                    <h3 className="text-sm font-semibold">{t('offers.modal.preview')}</h3>
                    {lineItems.map((li) => (
                        <PriceProbe key={li.priceId} priceId={li.priceId} currency={currency} onResolve={handlePriceResolve} />
                    ))}
                    {discountMode === 'existing' &&
                        promotionIds.map((id) => (
                            <PromoDiscountProbe
                                key={id}
                                promotionId={id}
                                storeId={storeId}
                                currency={currency}
                                onResolve={handlePromoDiscountResolve}
                            />
                        ))}
                    {lineItems.length === 0 ? (
                        <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                            {t('offers.modal.previewEmpty')}
                        </div>
                    ) : !pricesResolved ? (
                        <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                            {t('offers.modal.previewLoading')}
                        </div>
                    ) : (
                        <>
                            <ClubOfferCardView
                                preview
                                title={titleEn}
                                imageUrl={previewImage}
                                regularMinor={regularMinor}
                                clubMinor={clubMinor}
                                currency={currency}
                                expiryDate={expiry}
                                productCount={lineItems.length}
                            />
                            {missingDiscountInfo && (
                                <p className="text-muted-foreground text-xs">{t('offers.modal.previewExisting')}</p>
                            )}
                        </>
                    )}
                </div>
            </div>

            {!hideActions && (
                <div className="flex justify-end gap-2">
                    {onCancel && (
                        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                            {t('offers.modal.cancel')}
                        </Button>
                    )}
                    <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
                        {isSubmitting
                            ? isEdit
                                ? t('offers.modal.saving')
                                : t('offers.modal.creating')
                            : isEdit
                              ? t('offers.modal.saveChanges')
                              : t('offers.modal.submit')}
                    </Button>
                </div>
            )}
        </div>
    )
})
